import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

export interface MonitorPalette {
    ink: string;
    inkMuted: string;
    inkFaint: string;
    grid: string;
    gridStrong: string;
    forwardWash: string;
    pendingBand: string;
    card: string;
    chipBg: string;
    chipBorder: string;
    divider: string;
    modalOutline: string;
}

const DARK: MonitorPalette = {
    ink: '#f5f5f5',
    inkMuted: 'rgba(245, 245, 245, 0.62)',
    inkFaint: 'rgba(245, 245, 245, 0.38)',
    grid: 'rgba(245, 245, 245, 0.12)',
    gridStrong: 'rgba(245, 245, 245, 0.22)',
    forwardWash: 'rgba(245, 245, 245, 0.035)',
    pendingBand: 'rgba(245, 245, 245, 0.11)',
    card: '#222325',
    chipBg: '#202124',
    chipBorder: 'rgba(245, 245, 245, 0.25)',
    divider: 'rgba(245, 245, 245, 0.1)',
    modalOutline: 'rgba(245, 245, 245, 0.85)',
};

const LIGHT: MonitorPalette = {
    ink: '#111111',
    inkMuted: 'rgba(17, 17, 17, 0.66)',
    inkFaint: 'rgba(17, 17, 17, 0.45)',
    grid: 'rgba(17, 17, 17, 0.1)',
    gridStrong: 'rgba(17, 17, 17, 0.24)',
    forwardWash: 'rgba(17, 17, 17, 0.035)',
    pendingBand: 'rgba(17, 17, 17, 0.09)',
    card: '#eeeeee',
    chipBg: '#f5f5f5',
    chipBorder: 'rgba(17, 17, 17, 0.25)',
    divider: 'rgba(17, 17, 17, 0.1)',
    modalOutline: 'rgba(17, 17, 17, 0.85)',
};

export function paletteFor(mode: 'light' | 'dark'): MonitorPalette {
    return mode === 'dark' ? DARK : LIGHT;
}

export function useMonitorPalette(): MonitorPalette {
    const theme = useTheme();
    return useMemo(() => paletteFor(theme.palette.mode), [theme.palette.mode]);
}

export const BIN_COLORS = {
    cutLarge: '#2a78d6',
    cut: '#5aa0e0',
    hold: '#8a8a8a',
    hike: '#e34948',
    hikeLarge: '#c93a39',
};

export function binColor(deltaBp: number): string {
    if (deltaBp <= -50) return BIN_COLORS.cutLarge;
    if (deltaBp < 0) return BIN_COLORS.cut;
    if (deltaBp === 0) return BIN_COLORS.hold;
    if (deltaBp >= 50) return BIN_COLORS.hikeLarge;
    return BIN_COLORS.hike;
}

export function binOpacity(pPerMille: number): number {
    return 0.07 + 0.82 * Math.pow(pPerMille / 1000, 0.72);
}

export function directionColor(deltaBp: number): string {
    if (deltaBp <= -1) return BIN_COLORS.cutLarge;
    if (deltaBp >= 1) return BIN_COLORS.hike;
    return BIN_COLORS.hold;
}
